import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { staffAPI } from '../../services/api';

export interface Staff {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  specialties: string[];
  workingHours: any;
  isActive: boolean;
  metadata?: any;
  photoUrl?: string | null;
  calendarColor?: string;
  account?: { id?: string; email?: string; role?: string; isActive?: boolean };
  createdAt: string;
  updatedAt: string;
}

interface StaffState {
  requestId?: string;
  staff: Staff[];
  loading: boolean;
  error: string | null;
}

const initialState: StaffState = {
  staff: [],
  loading: false,
  error: null,
};

export const fetchStaff = createAsyncThunk(
  'staff/fetchStaff',
  async (_, { rejectWithValue }) => {
    try {
      const response = await staffAPI.getAll();
      return response.data.staff;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la încărcarea personalului');
    }
  }
);

export const createStaff = createAsyncThunk(
  'staff/createStaff',
  async (staffData: Partial<Staff>, { rejectWithValue }) => {
    try {
      const response = await staffAPI.create(staffData);
      return response.data.staff;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la adăugarea membrului');
    }
  }
);

export const updateStaff = createAsyncThunk(
  'staff/updateStaff',
  async ({ id, data }: { id: string; data: Partial<Staff> }, { rejectWithValue }) => {
    try {
      const response = await staffAPI.update(id, data);
      return response.data.staff;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la actualizarea membrului');
    }
  }
);

export const deleteStaff = createAsyncThunk(
  'staff/deleteStaff',
  async (id: string, { rejectWithValue }) => {
    try {
      await staffAPI.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Eroare la ștergerea membrului');
    }
  }
);

const staffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {
    setBookingStaff: (state, action) => { state.staff = action.payload; },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaff.pending, (state, action) => {
        state.requestId = action.meta.requestId;
        state.loading = true;
      })
      .addCase(fetchStaff.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.staff = action.payload;
        state.error = null;
      })
      .addCase(fetchStaff.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createStaff.fulfilled, (state, action) => {
        state.staff.push(action.payload);
      })
      .addCase(updateStaff.fulfilled, (state, action) => {
        const index = state.staff.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.staff[index] = action.payload;
        }
      })
      .addCase(deleteStaff.fulfilled, (state, action) => {
        state.staff = state.staff.filter(s => s.id !== action.payload);
      });
  },
});

export const { clearError, setBookingStaff } = staffSlice.actions;
export default staffSlice.reducer;
